import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { requestPasswordReset } from "@/api/auth";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [devToken, setDevToken] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await requestPasswordReset(email);
      setDevToken(res.dev_reset_token || null);
      setSubmitted(true);
    } catch {
      // Always show success to prevent enumeration
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Lenavio" className="h-9 w-auto mx-auto" />
          <h2 className="text-xl font-semibold font-heading text-text-primary mt-6">
            Reset your password
          </h2>
          <p className="text-sm text-text-muted mt-1">
            We'll send a reset link to your email
          </p>
        </div>

        {submitted ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-success/10 border border-success/20 px-4 py-4 text-center">
              <p className="text-sm text-text-primary">
                If that email is registered, a reset link has been sent.
              </p>
            </div>
            {devToken && (
              <div className="rounded-lg bg-warning/10 border border-warning/30 px-4 py-3">
                <p className="text-xs text-text-muted mb-1">
                  Dev mode — use this token:
                </p>
                <Link
                  to={`/reset-password?token=${devToken}`}
                  className="text-xs text-accent break-all"
                >
                  {devToken}
                </Link>
              </div>
            )}
            <Link
              to="/login"
              className="flex items-center gap-1.5 justify-center text-sm text-text-muted hover:text-text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon={<Mail className="h-4 w-4" />}
              required
              autoFocus
            />
            <Button type="submit" isLoading={isLoading} className="w-full">
              Send reset link
            </Button>
            <Link
              to="/login"
              className="flex items-center gap-1.5 justify-center text-sm text-text-muted hover:text-text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </form>
        )}
      </motion.div>
    </div>
  );
}
